import {
  getSessionTokenOrRedirect,
  getShopifyGlobal,
} from '@bundles/libs/shopify/appBridge'
import { useAuthStore } from '@store/authStore'
import {
  CLIENT_AUTH_REDIRECT_ERROR,
  handleShopifyAuthFailure,
  isShopifyAuthFailure,
  normalizeReauthStatus,
} from '@utils/shopifyAuthFailure'
import type { UseQueryOptions } from 'react-query'
import { useQuery } from 'react-query'

const BACKEND_BASE_URL = import.meta.env.VITE_BACKEND_BASE_URL

export const useAppQuery = ({
  key,
  url,
  fetchInit = {
    headers: {
      // Bổ sung X-Requested-With để shopify_app backend nhận ra đây là 1 XHR
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  },
  reactQueryOptions,
}: {
  key: string[]
  url: string
  fetchInit?: A
  reactQueryOptions: UseQueryOptions
}) => {
  const fullUrl = url.startsWith('http') ? url : `${BACKEND_BASE_URL}${url}`
  const authToken = useAuthStore(state => state.token)
  const isHydrated = useAuthStore(state => state.isHydrated)
  const authReady =
    isHydrated && (Boolean(authToken) || Boolean(getShopifyGlobal()?.idToken))

  const fetchData = async () => {
    // Session tokens live ~60s: fetch a fresh one per request, never reuse the store copy.
    const token = await getSessionTokenOrRedirect()
    const mergedHeaders = {
      ...(fetchInit.headers || {}),
      Authorization: `Bearer ${token}`,
    }
    const response = await fetch(fullUrl, {
      ...fetchInit,
      headers: mergedHeaders,
    })

    if (response.status === 401) {
      const reauthStatus = normalizeReauthStatus(
        response.headers.get('X-Reauth-Status'),
      )
      const authError = {
        type: CLIENT_AUTH_REDIRECT_ERROR,
        message: `${CLIENT_AUTH_REDIRECT_ERROR}: HTTP 401 ${response.url || fullUrl}`,
      }
      handleShopifyAuthFailure(authError, reauthStatus)
      throw authError
    }

    const redirectedToInstall =
      response.redirected && response.url.includes('/auth/install')

    if (redirectedToInstall) {
      const authError = {
        type: CLIENT_AUTH_REDIRECT_ERROR,
        message: `${CLIENT_AUTH_REDIRECT_ERROR}: ${response.url}`,
      }
      handleShopifyAuthFailure(authError)
      throw authError
    }

    const contentType = response.headers.get('content-type') || ''

    if (!contentType.includes('application/json')) {
      throw new Error(
        `Expected JSON response from ${fullUrl}, received ${contentType || 'unknown content type'}`,
      )
    }

    return response.json()
  }

  const enabledFromOptions = reactQueryOptions?.enabled
  const enabled =
    authReady &&
    (typeof enabledFromOptions === 'boolean' ? enabledFromOptions : true)

  // Defaults only — a caller-provided retry/retryDelay must win.
  const defaultRetry = (failureCount: number, error: unknown) => {
    if (isShopifyAuthFailure(error)) {
      return false
    }

    if (
      error?.message?.includes('Failed to fetch') ||
      error?.message?.includes('NetworkError') ||
      error?.message?.includes('CORS')
    ) {
      return false
    }

    return failureCount < 2
  }

  return useQuery(key, fetchData, {
    ...reactQueryOptions,
    enabled,
    refetchOnWindowFocus: false,
    onError: (error: unknown) => {
      if (isShopifyAuthFailure(error)) {
        return
      }

      if (reactQueryOptions?.onError) {
        reactQueryOptions.onError(error)
      }
    },
    retry: reactQueryOptions?.retry ?? defaultRetry,
    retryDelay: reactQueryOptions?.retryDelay ?? 1000,
  })
}
